/**
 * Armazón de la aplicación: lo único que va en el paquete inicial junto con el enrutador.
 *
 * Contiene el salto al contenido, el encabezado, la navegación entre vistas y el marco donde se
 * dibuja la vista que corresponda.
 *
 * **La navegación llegó cuando existieron vistas a las que navegar.** Esta cabecera decía que
 * ponerla antes habría sido una lista de enlaces rotos, y era cierto; hoy las veinte vistas de
 * sesión están en la tabla del enrutador y a la mayoría solo se llegaba escribiendo la dirección.
 * Se dibuja en dos niveles y **solo con sesión**: quien no ha entrado ve el encabezado como estaba,
 * porque las cuatro pantallas del flujo de autenticación no tienen ninguna de estas vistas a mano.
 * La agrupación, el criterio con que se armó y la resolución de la pestaña activa viven en
 * `lib/navegacion.ts`; el dibujado, en `componentes/navegacion.tsx`.
 *
 * Aquí se montan además los dos proveedores de los que cuelga todo lo demás, y el orden importa:
 * el de sesión primero, porque el de reacciones necesita poder marcarla perdida al recibir un
 * `401`. Los dos van **dentro** del enrutador y no envolviéndolo, porque la reacción al `401`
 * navega y para eso necesita estar en su árbol.
 *
 * Los dos avisos globales se dibujan antes del contenido y no como superposición: el de recarga
 * y el de conectividad informan algo que afecta a toda la aplicación, así que empujan la página
 * en lugar de tapar el control que la persona estaba usando.
 */

import { Link, Outlet, isRouteErrorResponse, useRouteError } from 'react-router';

import { Navegacion } from '../componentes/navegacion';
import { ProveedorDeReacciones, useAvisosDeApi } from '../lib/reaccionesDeApi';
import { RUTA_DE_CONSENTIMIENTOS, RUTA_DE_INGRESO, RUTA_DE_REGISTRO } from '../lib/rutas';
import { ProveedorDeSesion, useSesion } from '../lib/sesion';

export function Armazon() {
  return (
    <ProveedorDeSesion>
      <ProveedorDeReacciones>
        <a className="aisalud-salto" href="#contenido">
          Saltar al contenido
        </a>

        <header className="aisalud-contenido aisalud-encabezado">
          <h1>AISalud</h1>
          <BarraDeSesion />
        </header>

        <NavegacionDeLaSesion />

        <AvisosGlobales />

        <main className="aisalud-contenido" id="contenido">
          <Outlet />
        </main>
      </ProveedorDeReacciones>
    </ProveedorDeSesion>
  );
}

/**
 * Quién está dentro, y la acción que corresponde.
 *
 * Durante la fase de resolución no se dibuja ninguna de las dos alternativas. Mostrar «ingresar»
 * mientras se sabe si hay sesión haría parpadear el encabezado en cada carga de quien ya está
 * dentro, que es la mayoría de las cargas.
 */
function BarraDeSesion() {
  const { estado, salir } = useSesion();

  if (estado.fase === 'resolviendo') {
    return (
      <p className="aisalud-encabezado__sesion" role="status">
        Comprobando tu sesión…
      </p>
    );
  }

  if (estado.fase === 'sinSesion') {
    return (
      <nav aria-label="Sesión" className="aisalud-encabezado__sesion">
        <Link to={RUTA_DE_INGRESO}>Ingresar</Link>
        <Link to={RUTA_DE_REGISTRO}>Crear cuenta</Link>
      </nav>
    );
  }

  return (
    <nav aria-label="Sesión" className="aisalud-encabezado__sesion">
      {/* El correo identifica la cuenta cuando no hay nombre declarado, que es el caso de toda
          cuenta recién creada: el perfil llega con el Requisito 2. */}
      <span>{estado.cuenta.displayName ?? estado.cuenta.email}</span>
      <Link to={RUTA_DE_CONSENTIMIENTOS}>Consentimientos</Link>
      <button className="aisalud-boton" onClick={() => void salir()} type="button">
        Salir
      </button>
    </nav>
  );
}

/**
 * La navegación, y la única condición para dibujarla: haber entrado.
 *
 * Sin sesión no se dibuja nada, ni durante la fase de resolución. Es lo mismo que hace la barra de
 * sesión de arriba y por el mismo motivo: aparecer y desaparecer en cada carga haría saltar el
 * contenido de la página hacia abajo justo cuando la persona ya está leyendo.
 *
 * La condición se comprueba aquí y no dentro del componente, y esa es la razón por la que
 * `componentes/navegacion.tsx` no consume el contexto de sesión: así se puede dibujar con solo un
 * enrutador de memoria, y la prueba que comprueba la marca de la pestaña activa no necesita montar la
 * sesión ni sustituir la API.
 */
function NavegacionDeLaSesion() {
  const { estado } = useSesion();

  if (estado.fase !== 'conSesion') return null;

  return <Navegacion />;
}

/**
 * Los dos avisos que afectan a toda la aplicación.
 *
 * El de versión obsoleta ofrece recargar y no recarga por su cuenta: una recarga automática
 * mientras alguien escribe un formulario se lleva lo escrito, que es justo lo que el criterio
 * 26.8 pide no hacer. El de conectividad no ofrece nada porque no hay nada que pulsar: se retira
 * solo en cuanto una petición vuelve a llegar.
 */
function AvisosGlobales() {
  const { clienteObsoleto, sinConexion } = useAvisosDeApi();

  if (!clienteObsoleto && !sinConexion) return null;

  return (
    <div className="aisalud-contenido">
      {clienteObsoleto && (
        <div className="aisalud-aviso aisalud-aviso--fallo" role="alert">
          <p>
            Hay una versión nueva de AISalud y esta ya no está vigente. Recarga la página para
            seguir usándola.
          </p>
          <button
            className="aisalud-boton aisalud-boton--principal"
            onClick={() => {
              window.location.reload();
            }}
            type="button"
          >
            Recargar la página
          </button>
        </div>
      )}

      {sinConexion && (
        <div className="aisalud-aviso aisalud-aviso--fallo" role="status">
          <p>
            Perdimos la conexión con el servidor. Lo que hayas escrito sigue en el formulario:
            vuelve a intentarlo cuando tengas señal.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Estado de carga de la vista diferida. Se muestra mientras el fragmento de la ruta viaja por
 * la red, que en el primer dibujado es el único momento en que la pantalla estaría vacía.
 */
export function Cargando() {
  return (
    <main className="aisalud-contenido" id="contenido">
      <p role="status">Cargando…</p>
    </main>
  );
}

/**
 * Último recurso ante un error que ninguna vista atrapó. No muestra el error técnico: un
 * mensaje en español y nada más.
 *
 * El identificador de petición sí se muestra cuando lo hay, que es lo único útil que la persona
 * puede aportar al reportar el problema. Se lee por forma y no con `instanceof ErrorDeApi`,
 * porque este límite atrapa cualquier cosa que haya llegado hasta aquí, incluido lo que el
 * enrutador convirtió en respuesta de ruta.
 */
export function LimiteDeError() {
  const error = useRouteError();
  const esRutaInexistente = isRouteErrorResponse(error) && error.status === 404;
  const idDePeticion =
    typeof error === 'object' &&
    error !== null &&
    'idDePeticion' in error &&
    typeof error.idDePeticion === 'string' &&
    error.idDePeticion !== ''
      ? error.idDePeticion
      : null;

  return (
    <main className="aisalud-contenido" id="contenido">
      <div className="aisalud-tarjeta">
        <h2>{esRutaInexistente ? 'No encontramos esa página' : 'Algo no funcionó'}</h2>
        <p>
          {esRutaInexistente
            ? 'La dirección que abriste no existe en AISalud.'
            : 'Vuelve a intentarlo en unos minutos. Si el problema sigue, recarga la página.'}
        </p>
        {idDePeticion !== null && (
          <p>
            Identificador de la petición: <code>{idDePeticion}</code>
          </p>
        )}
        <a href="/">Volver al inicio</a>
      </div>
    </main>
  );
}
